import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath, branding } = useTenant();
  
  // Check auth on mount and when location changes
  useEffect(() => {
    checkAuth();
  }, [location.pathname]);
  
  // Load cart count for both logged-in users and guests
  useEffect(() => {
    loadCartCount();
  }, [user, location.pathname]);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadCartCount = async () => {
    if (user) {
      // Logged-in user: fetch from server
      try {
        const response = await api.get('/cart');
        setCartCount(response.data.length);
      } catch (error) {
        console.error('Failed to load cart count');
      }
    } else {
      // Guest user: get from localStorage
      const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      setCartCount(guestCart.length);
    }
  };
  
  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(null);
      toast.success('Logged out successfully');
      navigate(tenantPath('/'));
    } catch (error) {
      toast.error('Failed to logout');
    }
  };
  
  const categories = [
    { name: 'Custom Shafts', path: '/category/shafts' },
    { name: 'Drivers', path: '/category/drivers' },
    { name: 'Fairways', path: '/category/fairways' },
    { name: 'Hybrids', path: '/category/hybrids' },
    { name: 'Irons', path: '/category/irons' },
    { name: 'Wedges', path: '/category/wedges' },
    { name: 'Putters', path: '/category/putters' },
    { name: 'Pre-Owned', path: '/category/pre-owned' },
    { name: 'Fitting Wizard', path: '/fitting-wizard', highlight: true },
  ];

  // Get colors from branding with fallbacks
  const primaryColor = branding?.primary_color || '#4A6741';
  const accentColor = branding?.accent_color || '#D4A55A';
  const headerBgColor = branding?.header_bg_color || '#FFFFFF';
  const storeName = branding?.name || 'Golfshop.fit';
  const logoUrl = branding?.logo_url;
  const hideLogo = branding?.hide_header_logo || false;
  const hideName = branding?.hide_header_name || false;
  const headerTagline = branding?.header_tagline || '';

  // Determine text color based on header background brightness
  const isLightBackground = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128;
  };
  
  const textColorClass = isLightBackground(headerBgColor) ? 'text-[#2C3E2D]' : 'text-white';
  const secondaryTextClass = isLightBackground(headerBgColor) ? 'text-[#5A6B5C]' : 'text-gray-300';
  const borderColorClass = isLightBackground(headerBgColor) ? 'border-[#E8E4DC]' : 'border-white/20';
  
  return (
    <header 
      className={`sticky top-0 z-50 backdrop-blur-md border-b ${borderColorClass}`}
      style={{ backgroundColor: `${headerBgColor}f5` }}
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        {/* Top bar */}
        <div className="flex items-center justify-between h-20">
          <Link to={tenantPath('/')} className="flex items-center gap-4" data-testid="site-logo">
            {!hideLogo && (
              logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={storeName} 
                  className="h-16 w-16 rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                <img src="/logo3.png" alt={storeName} className="h-20 w-20 rounded-full object-cover" />
              )
            )}
            {/* Fallback logo placeholder (hidden by default if custom logo exists) */}
            {!hideLogo && logoUrl && (
              <div 
                className="h-16 w-16 rounded-full items-center justify-center text-white text-xl font-bold hidden"
                style={{ backgroundColor: primaryColor }}
              >
                {storeName.charAt(0)}
              </div>
            )}
            {!hideName && (
              <div className={`text-xl font-black uppercase tracking-tight leading-tight ${textColorClass}`} style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="header-store-name">
                {storeName.includes(' ') ? (
                  <>
                    {storeName.split(' ').slice(0, -1).join(' ')}<br/>
                    <span className={`${secondaryTextClass} text-sm tracking-[0.15em] font-bold`} data-testid="header-tagline">
                      {headerTagline || storeName.split(' ').slice(-1)[0]}
                    </span>
                  </>
                ) : (
                  <>
                    {storeName}
                    {headerTagline && (
                      <>
                        <br/>
                        <span className={`${secondaryTextClass} text-sm tracking-[0.15em] font-bold`} data-testid="header-tagline">
                          {headerTagline}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {/* If the name itself is hidden, the tagline still needs somewhere to render */}
            {hideName && headerTagline && (
              <div className={`${secondaryTextClass} text-sm tracking-[0.1em] font-bold uppercase`} data-testid="header-tagline">
                {headerTagline}
              </div>
            )}
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center">
            {user && user.role === 'admin' && (
              <Link 
                to={tenantPath('/admin')} 
                className={`${secondaryTextClass} hover:opacity-80 font-medium transition-colors mr-8`}
                style={{ ':hover': { color: primaryColor } }}
              >
                Admin
              </Link>
            )}
            
            {/* Cart - always visible */}
            <Link 
              to={tenantPath('/cart')} 
              className={`relative ${secondaryTextClass} hover:opacity-80 transition-colors mr-8`} 
              data-testid="cart-link"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span 
                  className="absolute -top-2 -right-2 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" 
                  style={{ backgroundColor: accentColor }}
                  data-testid="cart-count"
                >
                  {cartCount}
                </span>
              )}
            </Link>
            
            {!isLoading && user ? (
              <>
                <Link 
                  to={tenantPath('/dashboard')} 
                  className={`${secondaryTextClass} hover:opacity-80 font-medium transition-colors mr-8`}
                >
                  Dashboard
                </Link>
                <button 
                  onClick={handleLogout} 
                  className={`${secondaryTextClass} hover:opacity-80 font-medium transition-colors`} 
                  data-testid="logout-button"
                >
                  Logout
                </button>
              </>
            ) : !isLoading ? (
              <Link 
                to={tenantPath('/login')} 
                className="h-10 px-6 text-white font-bold uppercase tracking-wider rounded-lg hover:opacity-90 transition-all flex items-center" 
                style={{ backgroundColor: primaryColor }}
                data-testid="login-button"
              >
                Login
              </Link>
            ) : null}
          </nav>
          
          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={textColorClass}
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        
        {/* Desktop Categories */}
        <nav className={`hidden lg:flex items-center justify-center gap-12 py-5 border-t ${borderColorClass}`}>
          {categories.map((cat) => (
            <Link
              key={cat.path}
              to={tenantPath(cat.path)}
              className={`text-sm font-bold transition-colors uppercase tracking-[0.15em] relative group px-2`}
              style={{ color: cat.highlight ? accentColor : (isLightBackground(headerBgColor) ? '#5A6B5C' : '#E5E7EB') }}
              data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {cat.name}
              <span 
                className="absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full"
                style={{ backgroundColor: accentColor }}
              ></span>
            </Link>
          ))}
        </nav>
      </div>
      
      {/* Mobile menu */}
      {isMenuOpen && (
        <div 
          className={`lg:hidden border-t ${borderColorClass}`} 
          style={{ backgroundColor: headerBgColor }}
          data-testid="mobile-menu"
        >
          <nav className="container mx-auto px-6 py-6 space-y-4">
            {categories.map((cat) => (
              <Link
                key={cat.path}
                to={tenantPath(cat.path)}
                onClick={() => setIsMenuOpen(false)}
                className={`block ${secondaryTextClass} hover:opacity-80 font-medium transition-colors`}
              >
                {cat.name}
              </Link>
            ))}
            <div className={`pt-4 border-t ${borderColorClass} space-y-4`}>
              {/* Cart - always visible */}
              <Link 
                to={tenantPath('/cart')} 
                onClick={() => setIsMenuOpen(false)} 
                className={`block ${secondaryTextClass} hover:opacity-80 font-medium transition-colors`}
              >
                Cart {cartCount > 0 && `(${cartCount})`}
              </Link>
              
              {!isLoading && user ? (
                <>
                  {user.role === 'admin' && (
                    <Link 
                      to={tenantPath('/admin')} 
                      onClick={() => setIsMenuOpen(false)} 
                      className={`block ${secondaryTextClass} hover:opacity-80 font-medium transition-colors`}
                    >
                      Admin
                    </Link>
                  )}
                  <Link 
                    to={tenantPath('/dashboard')} 
                    onClick={() => setIsMenuOpen(false)} 
                    className={`block ${secondaryTextClass} hover:opacity-80 font-medium transition-colors`}
                  >
                    Dashboard
                  </Link>
                  <button 
                    onClick={() => { handleLogout(); setIsMenuOpen(false); }} 
                    className={`block ${secondaryTextClass} hover:opacity-80 font-medium transition-colors w-full text-left`}
                  >
                    Logout
                  </button>
                </>
              ) : !isLoading ? (
                <Link 
                  to={tenantPath('/login')} 
                  onClick={() => setIsMenuOpen(false)} 
                  className="block font-bold transition-colors"
                  style={{ color: primaryColor }}
                >
                  Login
                </Link>
              ) : null}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
